<script lang="ts">
  import { selectedCustomerAccount } from "/@/stores/CustomerAccount"
  import DetailCell from "/@lib/components/DetailCell.svelte"
  import { twApiClient } from "/@lib/tastyworks-rest"
  import {
    formatAccountType,
    formatAddress,
    formatBirthDate,
    formatDate,
    formatDateTimeWithZone,
    formatName,
    formatTradingLevel,
  } from "/@lib/utils/formats"
  import {
    accountTradeStatusUrl,
    accountsByAccountNumberUrl,
    customersByEmailUrl,
    salesforceCustomerProfileUrl,
    tableauCustomerRevenueUrl,
    userSessionsUrl,
    usersByUsernameUrl,
  } from "/@lib/utils/urls"
  import {
    Customer,
    RecoveryCodesStatus,
    UrlPathBuilder,
  } from "@tastyworks/tastyworks-api"
  import { Tooltip } from "@tastyworks/ui-library"

  type Props = {
    class?: string
  }

  let { class: className = "" }: Props = $props()

  const selectedProfile = $derived($selectedCustomerAccount)
  const authority = $derived(
    selectedProfile?.authorities?.find(
      (authority) =>
        authority.customerId.toString() === selectedProfile?.customer?.id &&
        selectedProfile?.account?.accountAuthorities.includes(authority.id)
    )
  )

  const redFlagRecordsPromise = $derived(
    twApiClient.AdminCustomerAccountService.fetchRedFlags(
      $selectedCustomerAccount.customer?.id
    ).then((itemsResponse) =>
      itemsResponse.items.map((item) => item.notes).join(", ")
    )
  )

  const isPrivateClient = $derived(
    selectedProfile?.customer?.isCherryClubMember ?? false
  )

  const user = $derived(selectedProfile?.user)
  const securityQuestionsPromise = $derived(
    user?.isCreated
      ? twApiClient.AdminCredentialService.fetchSecurityQuestions(user.id).then(
          (response) => response.items
        )
      : Promise.resolve([])
  )

  const recoveryCodesStatusPromise = $derived(
    user?.isCreated
      ? twApiClient.AdminCredentialService.fetchRecoveryCodesStatus(user.id)
      : Promise.resolve(null)
  )

  const recoveryCodesValue = (status: RecoveryCodesStatus | undefined) => {
    if (!status) return "Unavailable"
    if (!status.isTwoFactorEnabled) return "N/A"
    if (status.generatedAt === null) return "Not generated"
    return `${status.codesRemaining} / ${status.totalCodes}`
  }

  const recoveryCodeLockoutValue = (
    status: RecoveryCodesStatus | undefined
  ) => {
    if (!status) return "Unavailable"
    if (!status.isTwoFactorEnabled) return "N/A"
    return status.isRecoveryCodeLocked ? "Locked" : "Not Locked"
  }

  const createCacCustomerUrl = (customer: Customer | undefined) => {
    if (!customer) return null
    return new UrlPathBuilder(import.meta.env.VITE_TW_BASE_URL)
      .nested("admin")
      .nested("customers")
      .toUrl(customer?.id ?? "")
  }

  // The Tableau Customer Revenue Lookup dashboard only exists in Tableau's
  // production site, so outside production the row must never imply a
  // working link exists — even for a customer who does have an external id.
  const isProductionEnvironment =
    import.meta.env.VITE_ENVIRONMENT === "production"
  const CUSTOMER_REVENUE_UNAVAILABLE_MESSAGE = "Only available in production"

  const customerRevenueValue = (externalId: string | undefined) =>
    isProductionEnvironment ? externalId : CUSTOMER_REVENUE_UNAVAILABLE_MESSAGE

  const customerRevenueHref = (externalId: string | undefined) =>
    isProductionEnvironment ? tableauCustomerRevenueUrl(externalId) : undefined
</script>

<div class={className}>
  <div class="flex h-full flex-col items-center justify-start gap-y-2 pt-1">
    {#await redFlagRecordsPromise then notes}
      {#if notes}
        <a
          class="text-alerts-error"
          href={createCacCustomerUrl($selectedCustomerAccount.customer)}
          target="_blank"
          rel="noopener noreferrer">{notes}</a
        >
      {/if}
    {:catch error}
      {error.message}
    {/await}
    <DetailCell
      label="Account Number"
      value={selectedProfile?.account?.accountNumber}
    />
    <div class="grid w-full grid-cols-2 grid-rows-12 gap-x-5 gap-y-2">
      <DetailCell
        label="Name"
        value={formatName(
          selectedProfile?.customer?.firstName,
          selectedProfile?.customer?.lastName,
          selectedProfile?.customer?.middleName
        )}
        href={salesforceCustomerProfileUrl(selectedProfile?.customer?.extCrmId)}
      />
      <DetailCell
        label="Address"
        value={formatAddress(selectedProfile?.customer?.address)}
      />
      <DetailCell
        label="Mobile Phone"
        value={selectedProfile?.customer?.mobilePhoneNumber}
      />
      <DetailCell
        label="Home Phone"
        value={selectedProfile?.customer?.homePhoneNumber}
      />
      <DetailCell
        label="Work Phone"
        value={selectedProfile?.customer?.workPhoneNumber}
      />
      <DetailCell
        label="Account Opened On"
        value={formatDate(selectedProfile?.account?.openedAt)}
      />
      <DetailCell
        label="Email"
        value={selectedProfile?.user?.email || selectedProfile?.customer?.email}
        href={customersByEmailUrl(
          selectedProfile?.user?.email || selectedProfile?.customer?.email
        )}
      />
      <DetailCell
        label="Date of Birth"
        value={formatBirthDate(selectedProfile?.customer?.birthDate)}
      />
      <DetailCell label="Status" />
      <DetailCell
        label="Username"
        value={selectedProfile?.user?.username}
        href={usersByUsernameUrl(selectedProfile?.user?.username)}
      />
      <DetailCell label="LTA" value={authority?.authorityLevel} />
      <DetailCell
        label="Customer Nickname"
        value={selectedProfile?.user?.nickname}
      />
      <DetailCell
        label="Tax Number Type"
        value={selectedProfile?.customer?.taxNumberType}
      />
      <DetailCell
        label="Tax Number"
        value={selectedProfile?.customer?.taxNumber}
      />
      <DetailCell
        label="Registration Type"
        value={formatAccountType(selectedProfile?.account?.accountType)}
      />
      <DetailCell
        label="Account Type"
        value={selectedProfile?.tradingStatus?.equitiesMarginCalculationType}
        href={accountTradeStatusUrl(selectedProfile?.account?.accountNumber)}
      />
      <DetailCell
        label="Trading Level"
        value={formatTradingLevel(selectedProfile?.tradingStatus?.optionsLevel)}
        href={accountsByAccountNumberUrl(
          selectedProfile?.account?.accountNumber
        )}
      />
      <DetailCell
        label="Account Closed On"
        value={formatDate(selectedProfile?.account?.closedAt)}
      />
      <DetailCell
        label="Confirmed At"
        value={formatDate(selectedProfile?.user?.confirmedAt)}
      />
      <DetailCell
        label="Last Sign In At"
        value={formatDateTimeWithZone(selectedProfile?.user?.lastSignInAt)}
        href={userSessionsUrl(
          selectedProfile?.user?.email || selectedProfile?.customer?.email
        )}
      />
      <DetailCell
        label="Is Frozen"
        value={selectedProfile?.tradingStatus?.isFrozen}
        href={selectedProfile?.tradingStatus?.isFrozen
          ? accountTradeStatusUrl(selectedProfile?.account?.accountNumber)
          : undefined}
      />
      <DetailCell
        label="Is Closing Only"
        value={selectedProfile?.tradingStatus?.isClosingOnly}
        href={selectedProfile?.tradingStatus?.isClosingOnly
          ? accountTradeStatusUrl(selectedProfile?.account?.accountNumber)
          : undefined}
      />
      <DetailCell
        label="Customer Revenue"
        value={customerRevenueValue(selectedProfile?.customer?.externalId)}
        href={customerRevenueHref(selectedProfile?.customer?.externalId)}
      />
      <div
        class="flex w-full items-center justify-between border-t-[1px] border-black text-sm"
      >
        <p>Private Client</p>
        <span
          data-testid="Private Client value"
          class={isPrivateClient ? "text-alerts-error" : ""}
        >
          {isPrivateClient ? "Yes" : "No"}
        </span>
      </div>
    </div>
    {#await recoveryCodesStatusPromise then recoveryCodesResponse}
      <DetailCell
        label="Recovery Codes"
        value={recoveryCodesValue(recoveryCodesResponse?.data)}
      />
      <DetailCell
        label="Recovery Code Lockout"
        value={recoveryCodeLockoutValue(recoveryCodesResponse?.data)}
        valueClass={recoveryCodesResponse?.data?.isRecoveryCodeLocked
          ? "text-alerts-error"
          : ""}
      />
    {:catch}
      <DetailCell label="Recovery Codes" value="Unavailable" />
      <DetailCell label="Recovery Code Lockout" value="Unavailable" />
    {/await}
    {#await securityQuestionsPromise then securityQuestions}
      {#if securityQuestions.length > 0}
        {#each securityQuestions as { question, answer }}
          <div
            class="flex w-full items-center justify-between border-t-[1px] border-black text-sm"
          >
            <h1>Security Questions</h1>
            <Tooltip.Root delayDuration={0}>
              <Tooltip.Provider>
                <Tooltip.Trigger>
                  <strong>{question}</strong>
                </Tooltip.Trigger>
                <Tooltip.Content>{answer}</Tooltip.Content>
              </Tooltip.Provider>
            </Tooltip.Root>
          </div>
        {/each}
      {:else}
        <DetailCell label="Security Questions" value="None provided" />
      {/if}
    {:catch}
      <DetailCell label="Security Questions" value="Unavailable" />
    {/await}
  </div>
</div>
